import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Tooltip,
  Switch,
  Modal,
  message,
  Skeleton,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  DollarCircleOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import type { AiModelPricing } from '@/api/modelPricing';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { ModelPricingEditorDrawer } from './ModelPricingEditorDrawer';

const { Text } = Typography;

// Admin-only management UI for ai.AiModelPricing. Rates are append-only by
// convention: when prices change, INSERT a new row + set ExpirationDate on
// the prior row. Clicking "Add new rate for this model" preserves history;
// "Edit" should be reserved for typo / metadata fixes.
//
// Layout: groups rows by (modelName, provider). Within each group, the most
// recent EffectiveDate is the active rate (highlighted), older rows fade.

export const ModelPricingPage = observer(function ModelPricingPage() {
  const { modelPricingStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AiModelPricing | null>(null);
  const [seedFromModel, setSeedFromModel] = useState<{ modelName: string; provider: string } | null>(null);

  useEffect(() => {
    if (store.items.length === 0 && !store.isLoading) {
      void store.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openEdit(row: AiModelPricing | null, seed?: { modelName: string; provider: string }): void {
    setEditing(row);
    setSeedFromModel(seed ?? null);
    setEditorOpen(true);
    trackEvent('Pricing.EditOpened', { id: row?.id ?? 'new' });
  }

  function handleDelete(row: AiModelPricing): void {
    Modal.confirm({
      title: 'Soft-delete this pricing row?',
      content: (
        <div>
          <Text>
            <b>{row.displayName ?? row.modelName}</b> · {row.provider}
          </Text>
          <div style={{ marginTop: 8, fontSize: 12, color: colors.slate[500] }}>
            Sets <code>IsActive = false</code>. Audit-trail rows in PromptLog still
            reference this id; cost calculation will skip it from now on.
          </div>
        </div>
      ),
      okText: 'Deactivate',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await store.remove(row.id);
          message.success('Pricing row deactivated');
          trackEvent('Pricing.Deactivated', { id: row.id });
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to deactivate');
        }
      },
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="amber"
        icon={<DollarCircleOutlined />}
        title={
          <Space size={6} align="center">
            <span>Models &amp; pricing</span>
            <HelpHotspot topic="models.overview" />
          </Space>
        }
        subtitle="LLM pricing rates per (model × provider). Stored in ai.AiModelPricing — USD per 1,000,000 tokens. Append-only: insert a new row when rates change."
        count={store.grouped.length}
        countLabel={store.grouped.length === 1 ? 'model' : 'models'}
        actions={
          <Space>
            <Input.Search
              allowClear
              placeholder="Search by model name or provider…"
              style={{ width: 280 }}
              value={store.search}
              onChange={(e) => store.setSearch(e.target.value)}
              onSearch={() => void store.fetch()}
              prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
            />
            <Tooltip title="Show inactive (soft-deleted) rows too">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <Switch
                  size="small"
                  checked={store.showInactive}
                  onChange={(v) => store.setShowInactive(v)}
                />
                Inactive
              </span>
            </Tooltip>
            <Button
              icon={<ReloadOutlined />}
              loading={store.isLoading}
              onClick={() => void store.fetch()}
            >
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>
              New rate
            </Button>
          </Space>
        }
      />

      {store.error && (
        <Alert type="error" showIcon message="Failed to load pricing" description={store.error} closable />
      )}

      {/* Banner explaining the append-only convention */}
      <div
        style={{
          padding: '12px 14px',
          background: isDark ? `${colors.amber[700]}22` : colors.amber[100],
          border: `1px solid ${colors.amber[600]}66`,
          borderRadius: 10,
          fontSize: 12.5,
          color: isDark ? colors.amber[100] : colors.amber[700],
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <DollarCircleOutlined style={{ fontSize: 16, marginTop: 2 }} />
        <div>
          <b>Pricing rows are an audit trail.</b> When provider rates change, click{' '}
          <i>Add new rate</i> on the model card — that inserts a new row with a fresh{' '}
          <code>EffectiveDate</code> and sets the prior row's <code>ExpirationDate</code>. Use{' '}
          <i>Edit</i> only for metadata fixes (display name, notes); editing the rates
          themselves on a row that's already produced cost data will distort historical
          spend reports.
        </div>
      </div>

      {store.isLoading && store.items.length === 0 ? (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 5 }} />
        </SectionCard>
      ) : store.grouped.length === 0 ? (
        <SectionCard>
          <BrandedEmpty
            icon={<DollarCircleOutlined />}
            title={store.search ? 'No models match' : 'No pricing rows yet'}
            description={
              store.search
                ? 'Try clearing the search.'
                : 'Add the first rate to enable cost tracking on every LLM call.'
            }
            action={
              !store.search ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>
                  Add the first rate
                </Button>
              ) : undefined
            }
          />
        </SectionCard>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {store.grouped.map((g) => (
            <ModelGroupCard
              key={g.key}
              group={g}
              isDark={isDark}
              onEdit={(row) => openEdit(row)}
              onDelete={handleDelete}
              onAddRate={() =>
                openEdit(null, { modelName: g.modelName, provider: g.provider })
              }
            />
          ))}
        </div>
      )}

      <ModelPricingEditorDrawer
        open={editorOpen}
        editing={editing}
        seed={seedFromModel}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
          setSeedFromModel(null);
        }}
      />
    </div>
  );
});

// ── Per-model group card ───────────────────────────────────────────────────

interface GroupCardProps {
  group: { key: string; modelName: string; provider: string; rates: AiModelPricing[] };
  isDark: boolean;
  onEdit: (row: AiModelPricing) => void;
  onDelete: (row: AiModelPricing) => void;
  onAddRate: () => void;
}

const ModelGroupCard = observer(function ModelGroupCard({
  group,
  isDark,
  onEdit,
  onDelete,
  onAddRate,
}: GroupCardProps) {
  const now = Date.now();
  // Active rate = isActive AND effectiveDate <= now AND (expirationDate null OR > now).
  const activeRate = group.rates.find((r) => {
    if (!r.isActive) return false;
    const eff = new Date(r.effectiveDate).getTime();
    if (eff > now) return false;
    if (r.expirationDate && new Date(r.expirationDate).getTime() <= now) return false;
    return true;
  });

  const display = activeRate?.displayName ?? group.rates[0]?.displayName ?? group.modelName;

  return (
    <SectionCard
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 14,
              fontWeight: 600,
              color: isDark ? colors.slate[50] : colors.slate[900],
            }}
          >
            {group.modelName}
          </span>
          <StatusPill tone="slate">{group.provider}</StatusPill>
          {display && display !== group.modelName && (
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              {display}
            </Text>
          )}
        </span>
      }
      subtitle={
        <span style={{ fontSize: 12 }}>
          {group.rates.length} {group.rates.length === 1 ? 'rate row' : 'rate rows'}
          {activeRate ? (
            <>
              {' · '}
              <Text type="secondary" style={{ fontSize: 12 }}>
                Active rate effective {new Date(activeRate.effectiveDate).toLocaleDateString()}
              </Text>
            </>
          ) : (
            <>
              {' · '}
              <span style={{ color: colors.amber[600], fontWeight: 600 }}>
                No active rate — cost calculation will return null
              </span>
            </>
          )}
        </span>
      }
      actions={
        <Button size="small" icon={<PlusOutlined />} onClick={onAddRate}>
          Add new rate
        </Button>
      }
      noPadding
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '120px 110px 110px 110px 130px 90px 100px',
          gap: 0,
          padding: '0 0 8px',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
        }}
      >
        <span style={{ padding: '8px 14px' }}>Effective</span>
        <span style={{ padding: '8px 14px', textAlign: 'right' }}>Input / 1M</span>
        <span style={{ padding: '8px 14px', textAlign: 'right' }}>Output / 1M</span>
        <span style={{ padding: '8px 14px', textAlign: 'right' }}>Cached / 1M</span>
        <span style={{ padding: '8px 14px', textAlign: 'right' }}>Reasoning / 1M</span>
        <span style={{ padding: '8px 14px', textAlign: 'center' }}>Status</span>
        <span style={{ padding: '8px 14px', textAlign: 'right' }}>Actions</span>
      </div>
      <div>
        {group.rates.map((r) => (
          <RateRow
            key={r.id}
            rate={r}
            isActive={activeRate?.id === r.id}
            isDark={isDark}
            onEdit={() => onEdit(r)}
            onDelete={() => onDelete(r)}
          />
        ))}
      </div>
    </SectionCard>
  );
});

// ── Single rate row ────────────────────────────────────────────────────────

function RateRow({
  rate,
  isActive,
  isDark,
  onEdit,
  onDelete,
}: {
  rate: AiModelPricing;
  isActive: boolean;
  isDark: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const now = Date.now();
  const effDate = new Date(rate.effectiveDate);
  const expDate = rate.expirationDate ? new Date(rate.expirationDate) : null;
  const isExpired = expDate ? expDate.getTime() <= now : false;
  const isFuture = effDate.getTime() > now;
  const isInactive = !rate.isActive;

  const muted = isExpired || isInactive;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 110px 110px 110px 130px 90px 100px',
        gap: 0,
        alignItems: 'center',
        padding: '10px 0',
        background: isActive
          ? isDark ? `${colors.green[700]}22` : colors.green[100] + '55'
          : 'transparent',
        borderLeft: isActive ? `3px solid ${colors.green[600]}` : '3px solid transparent',
        opacity: muted ? 0.55 : 1,
        borderBottom: `1px solid ${isDark ? colors.slate[800] : colors.slate[100]}`,
      }}
    >
      <span
        style={{
          padding: '0 14px',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
          color: isDark ? colors.slate[200] : colors.slate[700],
        }}
      >
        <CalendarOutlined style={{ fontSize: 10, marginRight: 5, opacity: 0.6 }} />
        {effDate.toLocaleDateString()}
        {expDate && (
          <div style={{ fontSize: 10.5, color: colors.slate[500], marginTop: 2 }}>
            until {expDate.toLocaleDateString()}
          </div>
        )}
      </span>
      <Money value={rate.inputPer1MTokens} />
      <Money value={rate.outputPer1MTokens} />
      <Money value={rate.cachedInputPer1MTokens} />
      <Money value={rate.reasoningOutputPer1MTokens} />
      <span style={{ padding: '0 14px', textAlign: 'center' }}>
        {isActive ? (
          <StatusPill tone="green">Active</StatusPill>
        ) : isFuture ? (
          <StatusPill tone="amber">Future</StatusPill>
        ) : isExpired ? (
          <StatusPill tone="slate">Expired</StatusPill>
        ) : isInactive ? (
          <StatusPill tone="red">Inactive</StatusPill>
        ) : (
          <StatusPill tone="slate">—</StatusPill>
        )}
      </span>
      <span style={{ padding: '0 12px', textAlign: 'right' }}>
        <Space size={2}>
          <Tooltip title="Edit (use for metadata fixes only)">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} />
          </Tooltip>
          <Tooltip title={`Copy id #${rate.id}`}>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                void navigator.clipboard?.writeText(String(rate.id));
                message.success('Id copied');
              }}
            />
          </Tooltip>
          <Tooltip title="Soft-delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={onDelete}
            />
          </Tooltip>
        </Space>
      </span>
    </div>
  );
}

function Money({ value }: { value?: number | null }) {
  if (value == null) {
    return (
      <span
        style={{
          padding: '0 14px',
          textAlign: 'right',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11.5,
          color: colors.slate[400],
        }}
      >
        —
      </span>
    );
  }
  return (
    <span
      style={{
        padding: '0 14px',
        textAlign: 'right',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12.5,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      ${Number(value).toFixed(4)}
    </span>
  );
}
